import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { APP_CONFIG } from '../../../config/app.config';
import { Icon, IconSize } from '@shared/icons';

interface TutorialVideoProps {
  /** Primary MP4 (H.264) source. */
  src: string;
  /** Optional WebM source for smaller files in supporting browsers. */
  webmSrc?: string;
  /** Poster frame shown before the video can play. */
  poster?: string;
  /** Accessible label for the video. */
  ariaLabel?: string;
  /** When false (e.g. off-screen pager pane), pause playback. */
  active?: boolean;
}

/**
 * Short instructional clip for onboarding overlays.
 * Autoplays muted, loops, and has no native controls — matching the Figma preview frame.
 */
const TutorialVideo: React.FC<TutorialVideoProps> = ({
  src,
  webmSrc,
  poster,
  ariaLabel = 'Tutorial video',
  active = true,
}) => {
  const colors = APP_CONFIG.ui.theme.colors;
  const videoRef = useRef<HTMLVideoElement>(null);
  /** Only shown when autoplay is blocked — avoids a flash on mount/src change. */
  const [showPlayOverlay, setShowPlayOverlay] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (!active) {
      video.pause();
      return;
    }

    setShowPlayOverlay(false);

    const play = async () => {
      try {
        await video.play();
        setShowPlayOverlay(false);
      } catch {
        setShowPlayOverlay(true);
      }
    };

    void play();
  }, [src, webmSrc, active]);

  const handlePlayClick = async () => {
    const video = videoRef.current;
    if (!video) return;

    try {
      await video.play();
      setShowPlayOverlay(false);
    } catch {
      setShowPlayOverlay(true);
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: '8px',
        border: 4,
        borderColor: colors.border,
        bgcolor: colors.backgroundTint,
        overflow: 'hidden',
        boxShadow: '0px 0px 2px 0px rgba(0, 0, 0, 0.1)',
        aspectRatio: '1176/720',
      }}
    >
      <video
        ref={videoRef}
        key={src}
        muted
        loop
        playsInline
        autoPlay
        poster={poster}
        aria-label={ariaLabel}
        onPlaying={() => setShowPlayOverlay(false)}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      >
        {webmSrc && <source src={webmSrc} type="video/webm" />}
        <source src={src} type="video/mp4" />
      </video>

      {showPlayOverlay && (
        <Box
          component="button"
          type="button"
          onClick={handlePlayClick}
          aria-label="Play tutorial video"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 0,
            p: 0,
            m: 0,
            cursor: 'pointer',
            bgcolor: 'rgba(255, 255, 255, 0.35)',
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: colors.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 2,
            }}
          >
            <Icon icon="Play01" size={IconSize.LARGE} color="#fff" />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TutorialVideo;
